import { Injectable } from '@angular/core';

/**
 * Reference to MomentJS
 */
const moment = require('moment');

/**
 * DateUtilService
 *
 * @export DateUtilService
 */
@Injectable({
  providedIn: 'root',
})
export class DateUtilService {
  /**
   * A static Array of each month of the year
   */
  private static months: Array<string> = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  /**
   * Creates an instance of DateUtilService.
   */
  constructor() {}

  /**
   * getWeekNumber
   *
   * @param d date to get week number of
   * @returns string representing the week of the input date and
   */
  public static getWeekNumber(d: Date): string {
    return 'Week ' + moment(d).isoWeek() + '-' + moment(d).isoWeekYear();
  }

  /**
   *  getMonth
   *
   * @param d date to get Month of
   * @returns string representing the month of the input date and year
   */
  public static getMonth(d: Date): string {
    return (moment(d).month() + 1) + ' ' + moment(d).format('MMMM') + '-' + moment(d).year();
  }

  /**
   * getDates
   *
   * @param startDate date in string form
   * @param stopDate date in string form
   * @returns array of dates in string form between the two input strings
   */
  public static getDates(
    startDate: string,
    stopDate: string,
  ): Array<string> {
    const dateArray: Array<string> = new Array<string>();
    let currentDate: Date = new Date(startDate);
    const sDate: Date = new Date(stopDate);
    while (currentDate <= sDate) {
      dateArray.push(
        new Date(currentDate).toISOString().split('T')[0],
      );
      currentDate = this.addDays(currentDate, 1);
    }
    return dateArray;
  }

  /**
   * get Weeks
   *
   * @param startWeek week in number form
   * @param endWeek week in number form
   * @returns array of week number strings between the two input weeks
   */
  public static getWeeks(
    startWeek: number,
    startYear: number,
    endWeek: number,
    endYear: number,
  ): Array<string> {
    const weekArray: Array<string> = new Array<string>();
    const trueEndWeek: number = (endYear - startYear) * 52 + endWeek;
    for (let i: number = startWeek; i <= trueEndWeek; i++) {
      const currentWeek = i % 52;
      const currentYear = Math.floor(i / 52) + startYear;
      weekArray.push('Week ' + currentWeek + '-' + currentYear);
    }
    return weekArray;
  }

  /**
   * getMonths
   *
   * @param startMonth month in string form
   * @param endMonth month in string form
   * @returns array of month strings between the two input months
   */
  public static getMonths(
    startMonth: string,
    endMonth: string,
  ): Array<string> {
    if (
      startMonth === null ||
      startMonth === undefined ||
      startMonth === '' ||
      endMonth === null ||
      endMonth === undefined ||
      endMonth === ''
    ) {
      return [];
    }
    const monthArray: Array<string> = new Array<string>();
    const sM: number = this.months.indexOf(
      startMonth.split(' ')[1].split('-')[0],
    );
    const eM: number = this.months.indexOf(
      endMonth.split(' ')[1].split('-')[0],
    );
    const sMY: number = parseInt(
      startMonth.split(' ')[1].split('-')[1],
      10,
    );
    const eMY: number = parseInt(
      endMonth.split(' ')[1].split('-')[1],
      10,
    );
    const trueEndMonth: number = (eMY - sMY) * 12 + eM;
    for (let i = sM; i <= trueEndMonth; i++) {
      const currentMonth: number = i % 12;
      const currentYear: number = Math.floor(i / 12) + sMY;
      monthArray.push(
        currentMonth +
          1 +
          ' ' +
          this.months[currentMonth] +
          '-' +
          currentYear,
      );
    }
    return monthArray;
  }

  /**
   * addDays
   *
   * @param currentDate input date to add days to
   * @param days number of days to add to input date
   * @returns new Date
   */
  public static addDays(currentDate: Date, days: number): Date {
    const date: Date = new Date(currentDate);
    date.setDate(date.getDate() + days);
    return date;
  }

  /**
   * getDateofWeek
   *
   * @param w week of the year
   * @param y year
   * @returns the corroponding date for a given week number
   */
  public static getDateOfWeek(w: number, y: number): Date {
    const d: number = 1 + (w - 1) * 7;
    return new Date(y, 0, d);
  }

  /**
   * toFormattedString
   *
   * @param date date to be formatted
   * @returns a string version of the input date in the format of 'yyyy-MM-dd'
   */
  public static toFormatedString(date: Date): string {
    const mm: number = date.getMonth() + 1;
    const dd: number = date.getDate();
    return [
      date.getFullYear(),
      '-',
      (mm > 9 ? '' : '0') + mm,
      '-',
      (dd > 9 ? '' : '0') + dd,
    ].join('');
  }
}
